<!--
  Ported from vendor-ref/mara/app/ui/src/components/nanome/EntryRow.vue.
  Kept: selection bar, collapse, eye, frame stepper (multi-model files), actions, zoom, more. Dropped: trajectory
  playback popover (server), spotlight. toggle-hidden / add-default / ZoomOnEntry became store actions.
-->
<script setup lang="ts">
import { computed } from 'vue';
import Button from 'primevue/button';
import { useLiteStore, type Entry } from '@nanome-lite/state';
import EntryComponents from './EntryComponents.vue';

const props = defineProps<{ entry: Entry; collapsed: boolean }>();
const emit = defineEmits<{
  'toggle-collapse': [entry: Entry];
  'toggle-actions': [e: Event, entry: Entry];
  'toggle-more': [e: Event, entry: Entry];
}>();

const store = useLiteStore();
const components = computed(() => store.componentsForEntry(props.entry.serial));
const allHidden = computed(() => components.value.length > 0 && components.value.every(c => c.hidden));
const showStepButtons = computed(() => props.entry.modelCount > 1);
const positionLabel = computed(() => `${props.entry.currentModelSerial}/${props.entry.modelCount}`);

function getEyeIcon() {
  if (components.value.every(c => c.hidden)) return 'i-tabler-eye-off';
  if (components.value.some(c => c.hidden)) return 'i-tabler-eye-minus';
  return 'i-tabler-eye';
}

function onEyeClick() {
  if (components.value.length) store.toggleEntryHidden(props.entry.serial);
  else store.resetDefaultComponents(props.entry.serial);
}

function onRowClick(e: MouseEvent) {
  if ((e.target as HTMLElement).closest('button')) return;
  store.toggleComponentsSelection(components.value.map(c => c.serial));
}
</script>

<template>
  <div class="flex flex-col">
    <div
      :data-selection="store.componentSelectionState(components.map(c => c.serial))"
      class="selectable-row flex items-center gap-2 px-4 py-1"
      @click="onRowClick"
    >
      <Button
        :icon="collapsed ? 'i-tabler-chevron-right' : 'i-tabler-chevron-down'"
        severity="secondary"
        size="small"
        rounded
        @click="emit('toggle-collapse', entry)"
      />
      <Button
        v-tooltip.top="components.length ? undefined : 'Add default representations'"
        :icon="getEyeIcon()"
        severity="secondary"
        size="small"
        rounded
        @click="onEyeClick"
      />
      <span :class="{ 'op-25': allHidden }" class="flex-1 truncate" :title="`${entry.name} (${entry.atomCount} atoms)`">
        {{ entry.name }}
      </span>
      <div class="flex gap-1 items-center">
        <template v-if="showStepButtons">
          <span class="text-xs op-75 ws-nowrap">{{ positionLabel }}</span>
          <div class="flex items-center gap-1 mr-2">
            <Button v-tooltip.top="'Previous model'" icon="i-tabler-arrow-narrow-left" severity="secondary" size="small" rounded @click="store.stepModel(entry.serial, -1)" />
            <Button v-tooltip.top="'Next model'" icon="i-tabler-arrow-narrow-right" severity="secondary" size="small" rounded @click="store.stepModel(entry.serial, +1)" />
          </div>
        </template>
        <Button v-tooltip.top="'Actions'" icon="i-tabler-layout-list" severity="secondary" size="small" rounded @click.prevent="emit('toggle-actions', $event, entry)" />
        <Button v-tooltip.top="'Zoom'" icon="i-tabler-search" severity="secondary" size="small" rounded @click="store.zoomEntry(entry.serial)" />
        <Button icon="i-tabler-dots" severity="secondary" size="small" rounded @click.prevent="emit('toggle-more', $event, entry)" />
      </div>
    </div>
    <div v-if="!collapsed">
      <EntryComponents :entry-serial="entry.serial" :entry-name="entry.name" />
    </div>
  </div>
</template>
